import { useEffect, useRef } from "react";
import * as THREE from "three";

const SPRITES = [
  ["/images/AK47_colour.png", 5],
  ["/images/ak47.png", 4],
  ["/images/303.png", 9],
  ["/images/wasser.png", 9],
  ["/images/cake.gif", 4],
  ["/images/cake2.gif", 5],
  ["/images/cake3.gif", 5],
  ["/images/star2.png", 13],
  ["/images/milky.png", 8],
  ["/images/sagan.png", 10],
];

// Ported from the WebGL particle canvas on jknr.me
export default function Starfield() {
  const ref = useRef(null);

  // Size/position the canvas over the emblem (mobile) or the bg2025 logo (desktop)
  const fit = () => {
    const el = ref.current;
    const target = document.querySelector(
      window.matchMedia("(min-width: 768px)").matches ? ".desktop-logo" : ".mobile-logo"
    );
    if (!el || !target) return null;
    const r = target.getBoundingClientRect();
    el.style.opacity = 0.25;
    el.style.top = `${r.top + window.scrollY}px`;
    el.style.left = `${r.left + window.scrollX}px`;
    el.style.width = `${r.width}px`;
    el.style.height = `${r.height}px`;
    return r;
  };

  useEffect(() => {
    const el = ref.current;
    const initial = fit();
    let w = initial ? initial.width : window.innerWidth;
    let h = initial ? initial.height : window.innerHeight;

    const camera = new THREE.PerspectiveCamera(75, w / h, 1, 3000);
    camera.position.z = 1000;
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000000, 0.001);

    const positions = new Float32Array(1000 * 3);
    for (let i = 0; i < positions.length; i++) positions[i] = 2000 * Math.random() - 1000;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const loader = new THREE.TextureLoader();
    const materials = SPRITES.map(([src, size]) => {
      const material = new THREE.PointsMaterial({
        size,
        map: loader.load(src),
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const points = new THREE.Points(geometry, material);
      points.rotation.set(6 * Math.random(), 6 * Math.random(), 6 * Math.random());
      scene.add(points);
      return material;
    });

    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(w, h);
    el.appendChild(renderer.domElement);

    const onResize = () => {
      const r = fit();
      if (!r || !r.width || !r.height) return;
      w = r.width;
      h = r.height;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("load", onResize);
    // logo images load after mount and change the layout
    const observer = new ResizeObserver(onResize);
    document.querySelectorAll(".desktop-logo, .mobile-logo").forEach((t) => observer.observe(t));
    onResize();

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const time = 5e-6 * Date.now();
      camera.lookAt(scene.position);
      scene.children.forEach((p, i) => {
        p.rotation.y = time * (i < 4 ? i + 1 : -(i + 1));
      });
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onResize);
      observer.disconnect();
      materials.forEach((m) => { m.map.dispose(); m.dispose(); });
      geometry.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: -1,
        overflow: "hidden",
      }}
    />
  );
}
